import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
} from '@mui/material';

/**
 * Reusable StatCard Component
 * Matches SuperAdmin dashboard design
 * @param {String} title - Card title
 * @param {Number|String} value - Main value to display
 * @param {String} growth - Growth percentage (e.g., "+12%")
 * @param {React.Component} icon - Icon component
 * @param {String} color - Color code for card theme
 */
const formatStatValue = (value) => {
  if (value === null || value === undefined || value === '' || value === 'NaN') {
    return '0';
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '0';
    return value > 1000 ? value.toLocaleString() : value;
  }

  return String(value);
};

const StatCard = ({ title, value, growth = null, icon: Icon, color = '#1976d2' }) => (
  <Card
    sx={{
      height: "100%",
      minHeight: 140,
      borderRadius: 3,
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
      },
      background: `linear-gradient(135deg, ${color}08 0%, ${color}05 100%)`,
      border: `1px solid ${color}20`,
    }}
  >
    <CardContent sx={{ p: { xs: 1.75, sm: 2.25, md: 3 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25, md: 3 } } }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: { xs: 1, sm: 1.5 }, mb: 2, minWidth: 0 }}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0, width: "100%" }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color,
              mb: 1,
              lineHeight: 1.15,
              fontSize: 'clamp(1.1rem, 1.6vw, 2rem)',
              minWidth: 0,
              width: '100%',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {formatStatValue(value)}
          </Typography>
          {growth ? (
            <Chip
              label={growth}
              color="success"
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: '#e8f5e8',
                color: '#2e7d32',
              }}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}15`,
            p: 1.5,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: 0,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default StatCard;
